import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import useInterval from 'hooks/useInterval'
import { dateDifference } from 'lib/datetime/dateUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const CountdownContainer = styled(Group)`
  padding: ${rem(12)} ${rem(28)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(40)};
  }
`

const TimerContainer = styled(Group)`
  width: ${rem(46)};

  ${mediaQueryUp.tablet} {
    width: ${rem(82)};
  }
`

const CountdownPrefixMobile = styled.span`
  font-style: ${props => props.theme.font.secondaryAlternate.fontStyle};
  font-family: ${props => props.theme.font.secondaryAlternate.family};
  font-weight: ${props => props.theme.font.secondaryAlternate.weight.regular};
  font-size: ${rem(14)};
  white-space: nowrap;
`

interface TimerProps {
  value: string
  label: string
}
function Timer(props: TimerProps) {
  const { value, label } = props

  return (
    <TimerContainer direction="vertical" gap={4} horizontalAlign="center">
      <Heading variant="heading2">
        {value}
      </Heading>
      <BodyText variant="medium" format="uppercase">
        {label}
      </BodyText>
    </TimerContainer>
  )
}

interface Props {
  startDate?: string
}

function WeddingCountdownTimer(props: Props) {
  const { startDate } = props
  const intl = useIntl()

  const [counter, setCounter] = useState(0)

  const { days, hours, minutes, seconds, finished } = useMemo(() => {
    if (!startDate) return { days: '00', hours: '00', minutes: '00', seconds: '00', finished: true }

    const remainingTime = dateDifference(new Date(startDate))
    return {
      days: `${remainingTime.days > 9 ? remainingTime.days : `0${remainingTime.days}`}`,
      hours: `${remainingTime.hours > 9 ? remainingTime.hours : `0${remainingTime.hours}`}`,
      minutes: `${remainingTime.minutes > 9 ? remainingTime.minutes : `0${remainingTime.minutes}`}`,
      seconds: `${remainingTime.seconds > 9 ? remainingTime.seconds : `0${remainingTime.seconds}`}`,
      finished: remainingTime.days === 0 && remainingTime.hours === 0 && remainingTime.minutes === 0 && remainingTime.seconds === 0,
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- counter is an intentional tick trigger, not a real dep
  }, [startDate, counter])

  useInterval(
    () => {
      setCounter(prev => prev + 1)
    },
    1000,
    { disabled: finished },
  )

  return (
    <CountdownContainer direction="horizontal" gap={12} horizontalAlign="center" verticalAlign="center">
      {/* Tablet only */}
      <CSSBreakpoint min="tablet"><Heading variant="heading3alt">
        {intl.formatMessage({ defaultMessage: 'See you in', id: 'Qo_YILPu45DqDBTQ0bQI', description: 'Countdown prefix shown before wedding event time remaining.' })}
      </Heading>
      </CSSBreakpoint>

      {/* Mobile only */}
      <CSSBreakpoint max="mobile"><CountdownPrefixMobile>
        {intl.formatMessage({ defaultMessage: 'See you in', id: 'k6B2-fMIx-beWKmBDYSw', description: 'Mobile countdown prefix shown before wedding event time remaining.' })}
      </CountdownPrefixMobile>
      </CSSBreakpoint>
      <Group direction="horizontal" gap={16} horizontalAlign="center">
        <Timer value={days} label={intl.formatMessage({ defaultMessage: 'Days', id: 'Zc_nYJSkvPpYV9EnHCxP', description: 'Label for days remaining in the wedding countdown timer.' })} />

        {/* Tablet only */}
        <CSSBreakpoint min="tablet"><Timer value={hours} label={intl.formatMessage({ defaultMessage: 'Hours', id: 'rHbpn6kzvcxt_PQQbS8A', description: 'Label for hours remaining in the wedding countdown timer.' })} /></CSSBreakpoint>
        <CSSBreakpoint min="tablet"><Timer value={minutes} label={intl.formatMessage({ defaultMessage: 'Minutes', id: '0Z6hpuXSeks-QciSpn4v', description: 'Label for minutes remaining in the wedding countdown timer.' })} /></CSSBreakpoint>
        <CSSBreakpoint min="tablet"><Timer value={seconds} label={intl.formatMessage({ defaultMessage: 'Seconds', id: 'FdoHF_Bdi1KoB9NpgN6m', description: 'Label for seconds remaining in the wedding countdown timer.' })} /></CSSBreakpoint>

        {/* Mobile only */}
        <CSSBreakpoint max="mobile"><Timer value={hours} label={intl.formatMessage({ defaultMessage: 'Hrs', id: '5Mt0VeDgUeqcKalwmb-G', description: 'Short label for hours remaining in the mobile wedding countdown timer.' })} /></CSSBreakpoint>
        <CSSBreakpoint max="mobile"><Timer value={minutes} label={intl.formatMessage({ defaultMessage: 'Min', id: '__DEg4928VTX-CXCm8fc', description: 'Short label for minutes remaining in the mobile wedding countdown timer.' })} /></CSSBreakpoint>
        <CSSBreakpoint max="mobile"><Timer value={seconds} label={intl.formatMessage({ defaultMessage: 'Sec', id: '8aBh_jMQz8l5RgIECTuN', description: 'Short label for seconds remaining in the mobile wedding countdown timer.' })} /></CSSBreakpoint>
      </Group>
    </CountdownContainer>
  )
}

export default WeddingCountdownTimer
